import {
  useMutation,
  UseMutationResult,
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  AddExternalContactRequest,
  ClientStepActivityDto,
  ExternalContactDto,
  ExternalStepRequestBody,
  ExternalStepRequestPreviewDto,
  SetTaskResolutionStepEtaRequest,
  AddTaskResolutionStepRequest,
  AssignAbatementTaskRequest,
  AttachForm7000_3Response,
  ClientCitationDetailDto,
  ClientExpectedStateDto,
  ClientHeldCitationDto,
  CreateInspectionEventRequest,
  CreateInspectionEventResponse,
  ClientAbatementTaskDetailDto,
  ClientAbatementTaskDto,
  ClientEvidenceDto,
  ClientEvidenceProvenanceDto,
  ClientEvidenceReadLinkDto,
  ClientEvidenceIntegrityRowDto,
  ClientEvidenceSweepStatusDto,
  ConfirmInspectorAliasRequest,
  EvidenceVerificationState,
  CreateInspectorRequest,
  MineInspectorDirectoryDto,
  CandidateEvidenceRowDto,
  CorrespondencePacketDetailDto,
  CorrespondencePacketRowDto,
  CreateCorrespondencePacketRequest,
  LinkPacketEvidenceRequest,
  LinkedPacketEvidenceResponse,
  MarkPacketSentRequest,
  RecordPacketReplyRequest,
  UpdateCorrespondencePacketRequest,
  SaveFieldOfficeContactRequest,
  SaveInspectorContactRequest,
  ClearEvidenceLegalHoldRequest,
  ClientForm7000_3AttachmentDto,
  ClientInspectionEventDto,
  ClientMineDto,
  ClientMineMemberDto,
  ClientNotificationDto,
  ClientTaskPushbackDto,
  ClientViolationResponseDto,
  PortfolioMineDto,
  PortfolioViolationTrendPointDto,
  CompleteAbatementTaskRequest,
  ConfirmClientCitationRequest,
  ConfirmClientCitationResponse,
  ExtractForm7000_3Response,
  InternalDeadlinePolicyDto,
  AssignTaskFromObservationRequest,
  LinkTaskToCitationRequest,
  NoCitationTaskKind,
  TaskLinkedDto,
  InspectionEventV2Dto,
  InspectionTriageDto,
  PagedV2Response,
  PlaceEvidenceLegalHoldRequest,
  PushBackAbatementTaskRequest,
  ReissueAbatementTaskRequest,
  ReportTaskProgressRequest,
  SetInternalDeadlinePolicyRequest,
  SetInternalDeadlinePolicyResponse,
  TaskAssignedDto,
  UpdateTaskResolutionStepRequest,
  UploadedTaskEvidence,
} from '../../domain/entities/MineClient';
import { MineDetailDto, MineViolationDto, PagingRequest } from '../../domain/entities/MineMiner';
import { PagedResult } from '../../domain/entities/PagedResult';
import { apiClient, ExtendedAxiosRequestConfig } from '../../infrastructure/api/ApiClient';
import { httpStatusOf } from '../../infrastructure/api/ApiError';
import { API_ENDPOINTS } from '../../infrastructure/api/config';

/**
 * For the page-load reads whose page renders its own failed state (sprint-273 S006, goal 2). The
 * page's ErrorState says what failed and offers a retry, so the client's global toast would only
 * add the raw Axios text ("Request failed with status code 404") on top of it, and on a polled
 * read it would repeat every interval.
 */
const RENDERS_OWN_ERROR: ExtendedAxiosRequestConfig = { showErrorToast: false };

/** A 404 is an answer, not a fault: retrying it only delays the page's not-found state. */
const retryUnlessNotFound = (failureCount: number, error: Error): boolean =>
  httpStatusOf(error) === 404 ? false : failureCount < 2;

/**
 * Client-area queries (sprint-217 — epic-036 Phase 1 increment 1D goal 3).
 *
 * A module of its own rather than more of `useMineMiner.ts`, which is 3,985 lines of staff CRM.
 * Same reasoning as the backend's separate `IClientMineReader`: the customer-facing data path
 * should be readable in one sitting, because that is the file a tenancy bug would be in.
 *
 * 🔴 Every hook here is scoped by the SERVER. Nothing below filters by mine on the client — the
 * API resolves an active `MineMembership` from the database before it answers (DR-005 clause 2).
 * A client-side filter over a wider response would be a rendering convention protecting real
 * data, which is not protection at all.
 */

export const mineClientKeys = {
  all: ['mine-client'] as const,
  myMines: () => [...mineClientKeys.all, 'mines'] as const,
  mineDetail: (mineId: string) => [...mineClientKeys.all, 'mine', mineId] as const,
  violations: (mineId: string, paging: PagingRequest) =>
    [...mineClientKeys.all, 'mine', mineId, 'violations', paging] as const,
  citationDetail: (mineId: string, violationNo: string) =>
    [...mineClientKeys.all, 'mine', mineId, 'citation', violationNo] as const,
  inspectionEvents: (mineId: string, paging: PagingRequest) =>
    [...mineClientKeys.all, 'mine', mineId, 'inspection-events', paging] as const,
  evidence: (mineId: string, inspectionEventId: string | null, paging: PagingRequest) =>
    [...mineClientKeys.all, 'mine', mineId, 'evidence', inspectionEventId, paging] as const,
  inspectionEventsV2: (mineId: string) =>
    [...mineClientKeys.all, 'mine', mineId, 'inspection-events-v2'] as const,
  inspectionTriage: (mineId: string, inspectionEventId: string) =>
    [...mineClientKeys.all, 'mine', mineId, 'inspection-triage', inspectionEventId] as const,
  form7000_3: (mineId: string, inspectionEventId: string | null, paging: PagingRequest) =>
    [...mineClientKeys.all, 'mine', mineId, '7000-3', inspectionEventId, paging] as const,

  // ── Phase 3 (sprint-227, increment 3F) — the abatement surface ────────────────────────────
  // sprint-230 (4B): `status` joins the key — the review queue is this list at UnderAdminReview,
  // and a cached unfiltered page must not answer for the filtered one. sprint-254 (A1):
  // `assignedToMe` and `sort` join it for the same reason.
  tasks: (
    mineId: string,
    paging: PagingRequest,
    status?: string,
    assignedToMe?: boolean,
    sort?: string
  ) =>
    [
      ...mineClientKeys.all,
      'mine',
      mineId,
      'tasks',
      paging,
      status ?? 'all',
      assignedToMe ? 'mine' : 'everyone',
      sort ?? 'newest',
    ] as const,
  taskDetail: (mineId: string, abatementTaskId: string) =>
    [...mineClientKeys.all, 'mine', mineId, 'task', abatementTaskId] as const,
  members: (mineId: string) => [...mineClientKeys.all, 'mine', mineId, 'members'] as const,
  // sprint-279 S004 (F23 slice 5): the organization's outside contacts as this mine reaches them,
  // and one step's timeline. Under `all`, so every step write (which invalidates `all`) refreshes
  // an open timeline too.
  externalContacts: (mineId: string) =>
    [...mineClientKeys.all, 'mine', mineId, 'external-contacts'] as const,
  stepActivity: (mineId: string, taskResolutionStepId: string) =>
    [...mineClientKeys.all, 'mine', mineId, 'step-activity', taskResolutionStepId] as const,
  violationResponses: (mineId: string, paging: PagingRequest) =>
    [...mineClientKeys.all, 'mine', mineId, 'violation-responses', paging] as const,
  // sprint-231 (4C): the reconciliation queue.
  expectedStates: (mineId: string, paging: PagingRequest) =>
    [...mineClientKeys.all, 'mine', mineId, 'expected-states', paging] as const,
  // sprint-236/S003 (4D): the held-citation register.
  heldCitations: (mineId: string, paging: PagingRequest) =>
    [...mineClientKeys.all, 'mine', mineId, 'held-citations', paging] as const,
  notifications: (mineId: string, paging: PagingRequest) =>
    [...mineClientKeys.all, 'mine', mineId, 'notifications', paging] as const,
  unreadCount: (mineId: string) =>
    [...mineClientKeys.all, 'mine', mineId, 'notifications', 'unread-count'] as const,

  // ── Phase 4 (sprint-229, increment 4A) — the policy surface ──────────────────────────────
  internalDeadlinePolicy: (mineId: string) =>
    [...mineClientKeys.all, 'mine', mineId, 'internal-deadline-policy'] as const,

  // ── sprint-237 (4E-1 Goal 6) — evidence integrity and legal holds ────────────────────────
  //
  // 🔴 Under `all`, unlike the 7000-3 extract below, and that is the point: placing or clearing a
  // hold changes what this answers for EVERY file the hold reaches, not just the one the Admin was
  // looking at. A mine-wide freeze reaches files on tasks nobody has open. Sweeping the whole tree
  // is the only invalidation that cannot leave a stale "not held" on screen.
  evidenceProvenance: (mineId: string, evidenceBlobId: string) =>
    [...mineClientKeys.all, 'mine', mineId, 'evidence-blob', evidenceBlobId] as const,

  // Same subtree as the single read on purpose: the hold-change sweep described above must catch
  // the batch too, or a mine-wide freeze leaves a stale set of "not held" chips on an open task.
  evidenceProvenanceBatch: (mineId: string, idsKey: string) =>
    [...mineClientKeys.all, 'mine', mineId, 'evidence-blobs', idsKey] as const,

  // ── sprint-244 (4E-2 Goal 4) — the register ──────────────────────────────────────────────
  //
  // 🔴 Under `all` for the same reason the provenance key is: a hold placed or cleared anywhere
  // changes which files appear here, not just the one an Admin was looking at.
  evidenceIntegrity: (mineId: string, paging: PagingRequest) =>
    [...mineClientKeys.all, 'mine', mineId, 'evidence-integrity', paging] as const,
  evidenceSweepStatus: (mineId: string) =>
    [...mineClientKeys.all, 'mine', mineId, 'evidence-sweep-status'] as const,

  // ── sprint-244 (4E-2 Goal 7) — the recipient directory ───────────────────────────────────
  //
  // 🔴 Under `all`, like the register's key. Confirming one spelling changes who a citation on a
  // completely different surface resolves to — the answer is shared, not local to this page.
  correspondenceDirectory: (mineId: string) =>
    [...mineClientKeys.all, 'mine', mineId, 'correspondence-directory'] as const,

  // sprint-244/S005 (4E-2 Goal 7 part 2) — the envelope. Under `all` for the same reason as the
  // directory (a packet names shared identities), and with a packet-scoped prefix so the compose
  // page's mutations can invalidate ONE packet plus the list without touching the rest of the tree.
  correspondencePackets: (mineId: string) =>
    [...mineClientKeys.all, 'mine', mineId, 'correspondence-packets'] as const,
  correspondencePacketList: (mineId: string, paging: PagingRequest) =>
    [...mineClientKeys.correspondencePackets(mineId), 'list', paging.page, paging.pageSize] as const,
  correspondencePacket: (mineId: string, packetId: string) =>
    [...mineClientKeys.correspondencePackets(mineId), packetId] as const,
  correspondencePacketCandidates: (mineId: string, packetId: string) =>
    [...mineClientKeys.correspondencePackets(mineId), packetId, 'candidate-evidence'] as const,

  // ── sprint-252 (4H, DR-041) — the Legal portfolio ────────────────────────────────────────
  //
  // Siblings of `myMines`, not under `'mine', mineId`: the portfolio is self-scoped — the
  // server resolves the caller's own Legal grants and no mine id exists on the client to key
  // by. Under `all` so a membership change's sweep refreshes the portfolio too.
  portfolioMines: () => [...mineClientKeys.all, 'portfolio', 'mines'] as const,
  portfolioViolationTrends: (quarters: number) =>
    [...mineClientKeys.all, 'portfolio', 'violation-trends', quarters] as const,

  // 🔴 Deliberately NOT under `all`. The extract is the machine's read of a fixed document, not
  // mine state: attach and confirm invalidate the whole `all` tree on success, and a key inside
  // it would be swept into that refetch — a fresh Document Intelligence spend, and a drafts swap
  // in the middle of checking a multi-citation file.
  extractForm7000_3: (mineId: string, attachmentId: string) =>
    ['7000-3-extract', mineId, attachmentId] as const,
};

/**
 * The mines this user belongs to, with names.
 *
 * 🔴 This replaces reading the token's `mine_membership` claims. The claims are a rendering aid
 * that goes stale the moment a membership is revoked — they stay in an already-issued token
 * until it expires — and they carry ids only, which is why the shell showed a bare MSHA code
 * before this existed. `staleTime` is deliberately short for the same reason.
 */
export function useMyMines(): UseQueryResult<ClientMineDto[], Error> {
  return useQuery<ClientMineDto[], Error>({
    queryKey: mineClientKeys.myMines(),
    queryFn: async (): Promise<ClientMineDto[]> =>
      apiClient.get<ClientMineDto[]>(API_ENDPOINTS.MINE_CLIENT.MY_MINES),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * The Legal portfolio's mine set with per-mine violation roll-ups (sprint-252 — 4H, DR-041).
 *
 * 🔴 Legal-only server-side (`RequireMineClientLegalPortfolio`); no parameter because the
 * scope IS the caller — the server resolves their active Legal grants on every request. Pass
 * `enabled: false` when the signed-in user holds no Legal role, to spare a request that was
 * always going to 403.
 */
export function usePortfolioMines(options?: {
  enabled?: boolean;
}): UseQueryResult<PortfolioMineDto[], Error> {
  return useQuery<PortfolioMineDto[], Error>({
    queryKey: mineClientKeys.portfolioMines(),
    queryFn: async (): Promise<PortfolioMineDto[]> =>
      apiClient.get<PortfolioMineDto[]>(API_ENDPOINTS.MINE_CLIENT.PORTFOLIO_MINES),
    enabled: options?.enabled ?? true,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Trends v1 — the portfolio's published violations by calendar quarter, ascending and SPARSE
 * (sprint-252 — 4H, D-f). `quarters` is clamped 1–20 by the server; 8 is the server default.
 */
export function usePortfolioViolationTrends(
  quarters: number,
  options?: { enabled?: boolean }
): UseQueryResult<PortfolioViolationTrendPointDto[], Error> {
  return useQuery<PortfolioViolationTrendPointDto[], Error>({
    queryKey: mineClientKeys.portfolioViolationTrends(quarters),
    queryFn: async (): Promise<PortfolioViolationTrendPointDto[]> =>
      apiClient.get<PortfolioViolationTrendPointDto[]>(
        API_ENDPOINTS.MINE_CLIENT.PORTFOLIO_VIOLATION_TRENDS,
        { params: { quarters } }
      ),
    enabled: options?.enabled ?? true,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useClientMineDetail(
  mineId: string,
  options?: { enabled?: boolean }
): UseQueryResult<MineDetailDto, Error> {
  return useQuery<MineDetailDto, Error>({
    queryKey: mineClientKeys.mineDetail(mineId),
    queryFn: async (): Promise<MineDetailDto> =>
      apiClient.get<MineDetailDto>(
        API_ENDPOINTS.MINE_CLIENT.MINE_BY_ID(mineId),
        RENDERS_OWN_ERROR
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    // A membership can name a mine with no ingest row, so a 404 is an ordinary outcome for an
    // authorised caller rather than a transient fault. Retrying it just delays the empty state.
    retry: retryUnlessNotFound,
  });
}

export function useClientMineViolations(
  mineId: string,
  paging: PagingRequest,
  options?: { enabled?: boolean }
): UseQueryResult<PagedResult<MineViolationDto>, Error> {
  return useQuery<PagedResult<MineViolationDto>, Error>({
    queryKey: mineClientKeys.violations(mineId, paging),
    queryFn: async (): Promise<PagedResult<MineViolationDto>> =>
      apiClient.get<PagedResult<MineViolationDto>>(
        API_ENDPOINTS.MINE_CLIENT.MINE_VIOLATIONS(mineId),
        { ...RENDERS_OWN_ERROR, params: { page: paging.page, pageSize: paging.pageSize } }
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * One citation as the §5.9 pair (sprint-229, 4A/N3/U2 — the sprint's one API addition).
 *
 * 🔴 Each side is SEPARATELY nullable and both nulls are ordinary states: `mshaPublished` is null
 * for the weeks between a 7000-3 confirm and the weekly ingest publishing the violation (DR-019
 * clause 5), and `operatorAsserted` is null when no response is on file. Render each absence in
 * its own words — never fold the two into one status.
 *
 * A 404 means NEITHER side knows the number at this mine (or it belongs to a mine the caller
 * cannot see — the server deliberately does not distinguish). An ordinary outcome for a typo'd
 * URL, so it is not retried.
 */
export function useClientCitationDetail(
  mineId: string,
  violationNo: string
): UseQueryResult<ClientCitationDetailDto, Error> {
  return useQuery<ClientCitationDetailDto, Error>({
    queryKey: mineClientKeys.citationDetail(mineId, violationNo),
    queryFn: async (): Promise<ClientCitationDetailDto> =>
      apiClient.get<ClientCitationDetailDto>(
        API_ENDPOINTS.MINE_CLIENT.MINE_VIOLATION_DETAIL(mineId, violationNo),
        RENDERS_OWN_ERROR
      ),
    enabled: mineId.length > 0 && violationNo.length > 0,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    retry: retryUnlessNotFound,
  });
}

export function useClientInspectionEvents(
  mineId: string,
  paging: PagingRequest,
  options?: { enabled?: boolean }
): UseQueryResult<PagedResult<ClientInspectionEventDto>, Error> {
  return useQuery<PagedResult<ClientInspectionEventDto>, Error>({
    queryKey: mineClientKeys.inspectionEvents(mineId, paging),
    queryFn: async (): Promise<PagedResult<ClientInspectionEventDto>> =>
      apiClient.get<PagedResult<ClientInspectionEventDto>>(
        API_ENDPOINTS.MINE_CLIENT.MINE_INSPECTION_EVENTS(mineId),
        { params: { page: paging.page, pageSize: paging.pageSize } }
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Captured field evidence, optionally narrowed to one inspection event.
 *
 * `inspectionEventId` can only narrow — the server applies the mine filter regardless, so
 * another mine's event id returns an empty page rather than that mine's evidence.
 */
export function useClientEvidence(
  mineId: string,
  inspectionEventId: string | null,
  paging: PagingRequest,
  options?: { enabled?: boolean }
): UseQueryResult<PagedResult<ClientEvidenceDto>, Error> {
  return useQuery<PagedResult<ClientEvidenceDto>, Error>({
    queryKey: mineClientKeys.evidence(mineId, inspectionEventId, paging),
    queryFn: async (): Promise<PagedResult<ClientEvidenceDto>> =>
      apiClient.get<PagedResult<ClientEvidenceDto>>(
        API_ENDPOINTS.MINE_CLIENT.MINE_EVIDENCE(mineId),
        {
          params: {
            page: paging.page,
            pageSize: paging.pageSize,
            ...(inspectionEventId ? { inspectionEventId } : {}),
          },
        }
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * The MSHA 7000-3 forms attached at this mine, optionally narrowed to one inspection.
 *
 * Same narrowing semantics as `useClientEvidence`: `inspectionEventId` can only reduce the set.
 * The server applies the mine filter regardless.
 */
export function useClientForm7000_3Attachments(
  mineId: string,
  inspectionEventId: string | null,
  paging: PagingRequest,
  options?: { enabled?: boolean }
): UseQueryResult<PagedResult<ClientForm7000_3AttachmentDto>, Error> {
  return useQuery<PagedResult<ClientForm7000_3AttachmentDto>, Error>({
    queryKey: mineClientKeys.form7000_3(mineId, inspectionEventId, paging),
    queryFn: async (): Promise<PagedResult<ClientForm7000_3AttachmentDto>> =>
      apiClient.get<PagedResult<ClientForm7000_3AttachmentDto>>(
        API_ENDPOINTS.MINE_CLIENT.MINE_FORM_7000_3(mineId),
        {
          params: {
            page: paging.page,
            pageSize: paging.pageSize,
            ...(inspectionEventId ? { inspectionEventId } : {}),
          },
        }
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
}

export interface AttachForm7000_3Variables {
  mineId: string;
  inspectionEventId: string;
  file: File;
  /** The MSHA event number printed on the form. Optional — the document is the deliverable. */
  eventNo?: string;
}

/**
 * Attaches the 7000-3 MSHA sent the operator — the client area's ONE write.
 *
 * 🔴 The mine and the inspection travel in the URL, not the body. The server resolves a
 * membership for the mine and separately proves the inspection belongs to it; moving either into
 * the form data would put the cross-tenant question somewhere nothing checks it.
 *
 * On success this invalidates the whole client cache tree rather than one key. Two things change
 * at once and they are not both attachment queries: the form list gains a row, and the inspection
 * may have just learned its MSHA event number — which is rendered by the inspection-events query,
 * a different key entirely. Invalidating narrowly here would leave the event showing "Not
 * assigned" beside the form that had just assigned it.
 */
export function useAttachForm7000_3(): UseMutationResult<
  AttachForm7000_3Response,
  Error,
  AttachForm7000_3Variables
> {
  const queryClient = useQueryClient();

  return useMutation<AttachForm7000_3Response, Error, AttachForm7000_3Variables>({
    mutationFn: async (variables): Promise<AttachForm7000_3Response> => {
      const formData = new FormData();
      formData.append('file', variables.file);

      const eventNo = variables.eventNo?.trim();
      if (eventNo) {
        formData.append('eventNo', eventNo);
      }

      return apiClient.postForm<AttachForm7000_3Response>(
        API_ENDPOINTS.MINE_CLIENT.ATTACH_FORM_7000_3(
          variables.mineId,
          variables.inspectionEventId
        ),
        formData
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export interface Form7000_3AttachmentRef {
  mineId: string;
  attachmentId: string;
}

/**
 * OCRs an attached 7000-3 and returns the draft for the operator to check.
 *
 * 🔴 **A query, with every automatic behaviour switched off by hand.** It was a mutation
 * (sprint-217) so that react-query could never re-fire a call that spends money on a Document
 * Intelligence read — and that choice is what walled off J1 (sprint-228 N2): a mutation fired
 * from a mount effect loses its observer during StrictMode's simulated remount
 * (`MutationObserver.onUnsubscribe` detaches from the in-flight mutation and resubscribing never
 * re-attaches), so the 200 landed with nobody listening and the page pended forever.
 *
 * A query has the opposite mechanics and the same cost profile, now stated explicitly:
 *   - concurrent mounts JOIN one in-flight fetch, so the StrictMode double-mount spends one
 *     call — a guarantee the old ref guard only approximated;
 *   - `staleTime: Infinity` plus the three refetch switches: nothing re-fires on mount, focus,
 *     or reconnect;
 *   - `retry: false` (and `apiClient.post` does not retry either): a failed extract stays
 *     failed until a person asks again — `refetch()` is the "Try reading it again" button;
 *   - the key lives OUTSIDE `mineClientKeys.all` (see the key factory) so the confirm's
 *     invalidation sweep cannot re-spend it.
 *
 * Still a POST on the server's write allowlist despite storing nothing. Keeping the two sides
 * consistent means the cost is visible from either.
 */
export function useExtractForm7000_3(
  mineId: string,
  attachmentId: string,
  enabled: boolean
): UseQueryResult<ExtractForm7000_3Response, Error> {
  return useQuery<ExtractForm7000_3Response, Error>({
    queryKey: mineClientKeys.extractForm7000_3(mineId, attachmentId),
    queryFn: async (): Promise<ExtractForm7000_3Response> =>
      apiClient.post<ExtractForm7000_3Response>(
        API_ENDPOINTS.MINE_CLIENT.EXTRACT_FORM_7000_3(mineId, attachmentId)
      ),
    enabled,
    retry: false,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export interface ConfirmClientCitationVariables extends Form7000_3AttachmentRef {
  body: ConfirmClientCitationRequest;
}

/**
 * Files the citation the operator confirmed, and seeds the response to it.
 *
 * 🔴 This is the ADR-005 verify gate — the point of the whole pipeline. What is filed is what a
 * human affirmed, so the body carries the operator's field set rather than asking the server to
 * re-derive it from the extract.
 *
 * Invalidates the whole client cache tree rather than one key, for the same reason the attach
 * does: more than one surface changes and they are not all citation queries. A confirm can also
 * teach the inspection its MSHA event number, which is rendered from the inspection-events key.
 */
export function useConfirmClientCitation(): UseMutationResult<
  ConfirmClientCitationResponse,
  Error,
  ConfirmClientCitationVariables
> {
  const queryClient = useQueryClient();

  return useMutation<ConfirmClientCitationResponse, Error, ConfirmClientCitationVariables>({
    mutationFn: async (variables): Promise<ConfirmClientCitationResponse> =>
      apiClient.post<ConfirmClientCitationResponse>(
        API_ENDPOINTS.MINE_CLIENT.CONFIRM_CLIENT_CITATION(variables.mineId, variables.attachmentId),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

/* ────────────────────────────────────────────────────────────────────────────────────────
   The abatement surface — Phase 3 (sprint-227, increment 3F).

   The endpoints below have existed since increments 3A–3E and nothing in the browser could
   reach them: 3B, 3C, 3D and 3E all shipped API-only (TD-233). These hooks are the first
   consumers Phase 3's data has ever had.

   Every one is scoped by the SERVER, exactly as the 1D hooks above are. Nothing here filters
   by mine on the client.
   ──────────────────────────────────────────────────────────────────────────────────────── */

/**
 * The mine's abatement tasks, paged. `status` narrows within the mine (sprint-230, 4B) — the
 * review queue is this list at `UnderAdminReview`; omitted means every status, as before.
 *
 * sprint-254 (A1): `assignedToMe` narrows to the caller's own queue and `sort` reorders —
 * both SERVER-side, because the list is paged and a client-side filter or reorder of one
 * fetched page quietly lies past page one. The server resolves "me"; no user id travels.
 * `sort` is `'newest'` (default) or `'target'` (internal target, soonest first).
 */
export function useClientAbatementTasks(
  mineId: string,
  paging: PagingRequest,
  options?: { enabled?: boolean; status?: string; assignedToMe?: boolean; sort?: string }
): UseQueryResult<PagedResult<ClientAbatementTaskDto>, Error> {
  return useQuery<PagedResult<ClientAbatementTaskDto>, Error>({
    queryKey: mineClientKeys.tasks(
      mineId,
      paging,
      options?.status,
      options?.assignedToMe,
      options?.sort
    ),
    queryFn: async (): Promise<PagedResult<ClientAbatementTaskDto>> =>
      apiClient.get<PagedResult<ClientAbatementTaskDto>>(
        API_ENDPOINTS.MINE_CLIENT.MINE_TASKS(mineId),
        {
          ...RENDERS_OWN_ERROR,
          params: {
            page: paging.page,
            pageSize: paging.pageSize,
            ...(options?.status ? { status: options.status } : {}),
            ...(options?.assignedToMe ? { assignedToMe: true } : {}),
            ...(options?.sort ? { sort: options.sort } : {}),
          },
        }
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * One task in full — evidence, the pushback chain, and both deadlines.
 *
 * 🔴 The `deadline` block this returns is assembled by the server from TWO sources and they are
 * not interchangeable (sprint-227/S001 D1). Its DATES come from a live re-read of the MSHA
 * violation by natural key, because a stored copy goes stale the moment MSHA extends the term.
 * Its rung STAMPS come from the persisted calculation, because whether a rung has fired is
 * history a fresh derivation returns null by construction.
 *
 * That is why this is its own query rather than a filter over the list: the list's
 * `internalDeadlineUtc` is a denormalised copy, and the two legitimately differ while an ingest
 * has moved the term-due date and the recompute has not yet run.
 */
export function useClientAbatementTask(
  mineId: string,
  abatementTaskId: string,
  options?: { enabled?: boolean }
): UseQueryResult<ClientAbatementTaskDetailDto, Error> {
  return useQuery<ClientAbatementTaskDetailDto, Error>({
    queryKey: mineClientKeys.taskDetail(mineId, abatementTaskId),
    queryFn: async (): Promise<ClientAbatementTaskDetailDto> =>
      apiClient.get<ClientAbatementTaskDetailDto>(
        API_ENDPOINTS.MINE_CLIENT.MINE_TASK(mineId, abatementTaskId),
        RENDERS_OWN_ERROR
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0 && abatementTaskId.length > 0,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    // A task at another mine answers 404 rather than 403, deliberately — the server will not
    // confirm another tenant's row exists. Retrying it just delays the empty state.
    retry: retryUnlessNotFound,
  });
}

/** One task's pushback episodes, each with its ordered resolution chain. */
export function useClientTaskPushbacks(
  mineId: string,
  abatementTaskId: string,
  options?: { enabled?: boolean }
): UseQueryResult<ClientTaskPushbackDto[], Error> {
  return useQuery<ClientTaskPushbackDto[], Error>({
    queryKey: [...mineClientKeys.taskDetail(mineId, abatementTaskId), 'pushbacks'] as const,
    queryFn: async (): Promise<ClientTaskPushbackDto[]> =>
      apiClient.get<ClientTaskPushbackDto[]>(
        API_ENDPOINTS.MINE_CLIENT.MINE_TASK_PUSHBACKS(mineId, abatementTaskId)
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0 && abatementTaskId.length > 0,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * The mine's roster — who may be assigned work here, and how to render an assignee as a person.
 *
 * 🔴 Roster, never a user directory: the server scopes it by the route's mine.
 *
 * ⚠️ Revoked members are SERVED, not filtered. Filter on `isActive` when building an assignee
 * PICKER; use the whole set for a NAME LOOKUP, or a task assigned to somebody who has since left
 * renders as an unresolvable id.
 */
export function useClientMineMembers(
  mineId: string,
  options?: { enabled?: boolean }
): UseQueryResult<ClientMineMemberDto[], Error> {
  return useQuery<ClientMineMemberDto[], Error>({
    queryKey: mineClientKeys.members(mineId),
    queryFn: async (): Promise<ClientMineMemberDto[]> =>
      apiClient.get<ClientMineMemberDto[]>(API_ENDPOINTS.MINE_CLIENT.MINE_MEMBERSHIPS(mineId)),
    enabled: (options?.enabled ?? true) && mineId.length > 0,
    // Longer than the task queries: a roster changes when somebody is hired or leaves, not when
    // work moves. It is also read by every surface that renders an assignee's name.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * The citations this operator has filed a response against — what work can be assigned on.
 *
 * 🔴 **The read that makes `useAssignAbatementTask` callable** (TD-235). That mutation takes a
 * `violationResponseId` and no endpoint served one, so it sat here written and correct with nothing
 * able to call it.
 *
 * ⚠️ **Not `useClientMineViolations`.** That is MSHA's published record; this is what the operator
 * filed, and a response exists *before* the weekly ingest publishes its citation. Assigning off the
 * violations list would make the freshest citations — the ones in somebody's hand — unassignable.
 */
export function useClientViolationResponses(
  mineId: string,
  paging: PagingRequest,
  options?: { enabled?: boolean }
): UseQueryResult<PagedResult<ClientViolationResponseDto>, Error> {
  return useQuery<PagedResult<ClientViolationResponseDto>, Error>({
    queryKey: mineClientKeys.violationResponses(mineId, paging),
    queryFn: async (): Promise<PagedResult<ClientViolationResponseDto>> =>
      apiClient.get<PagedResult<ClientViolationResponseDto>>(
        API_ENDPOINTS.MINE_CLIENT.MINE_VIOLATION_RESPONSES(mineId),
        { params: { page: paging.page, pageSize: paging.pageSize } }
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0,
    // The task band rather than the roster's five minutes: a response appears the moment somebody
    // confirms a citation, and the Admin assigning work is often the person who just confirmed it.
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
}

/** This user's notifications at this mine, each with its per-channel delivery state. */
export function useClientNotifications(
  mineId: string,
  paging: PagingRequest,
  options?: { enabled?: boolean }
): UseQueryResult<PagedResult<ClientNotificationDto>, Error> {
  return useQuery<PagedResult<ClientNotificationDto>, Error>({
    queryKey: mineClientKeys.notifications(mineId, paging),
    queryFn: async (): Promise<PagedResult<ClientNotificationDto>> =>
      apiClient.get<PagedResult<ClientNotificationDto>>(
        API_ENDPOINTS.MINE_CLIENT.MINE_NOTIFICATIONS(mineId),
        { params: { page: paging.page, pageSize: paging.pageSize } }
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    // sprint-254 goal 3 (A3): the open feed refreshes itself on the same cadence as the
    // badge — an Admin's rejection should not need a navigation to appear.
    refetchInterval: 60 * 1000,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * How many notifications this user has not read at this mine — the badge's source.
 *
 * ⚠️ Reading this does NOT mark anything read. What the read endpoint writes is the only
 * proof-of-receipt the platform has, and the escalation sweep keys on it (DR-028/DR-029).
 */
export function useClientNotificationUnreadCount(
  mineId: string,
  options?: { enabled?: boolean }
): UseQueryResult<number, Error> {
  return useQuery<number, Error>({
    queryKey: mineClientKeys.unreadCount(mineId),
    queryFn: async (): Promise<number> => {
      const response = await apiClient.get<{ unreadCount: number }>(
        API_ENDPOINTS.MINE_CLIENT.MINE_NOTIFICATIONS_UNREAD_COUNT(mineId)
      );

      return response.unreadCount;
    },
    enabled: (options?.enabled ?? true) && mineId.length > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    // sprint-254 goal 3 (A3): the refresh story. An Admin's rejection lands while the
    // Supervisor's tab sits open; without a poll the badge only moves on focus/navigation.
    // Polling, not push, deliberately — the 3C Push channel and TD-248's durable outbox are
    // the platform's debt (pivot-review inputs), and a one-minute count read is cheap.
    refetchInterval: 60 * 1000,
  });
}

/**
 * Unread counts for SEVERAL mines at once — the multi-mine chrome's source (sprint-254
 * goal 3, A3). One entry per mine id, order preserved; each shares its cache entry with
 * the single-mine hook above, so entering a mine costs no extra read.
 *
 * 🔴 The caller renders these PER MINE and never sums them — a single number spanning
 * tenants is the one thing the client chrome exists to never show (sprint-227 rule;
 * DR-041 ruling 4 superseded it for ClientPortfolioPage ONLY).
 */
export function useClientNotificationUnreadCounts(
  mineIds: string[],
  options?: { enabled?: boolean }
): UseQueryResult<number, Error>[] {
  return useQueries({
    queries: mineIds.map((mineId) => ({
      queryKey: mineClientKeys.unreadCount(mineId),
      queryFn: async (): Promise<number> => {
        const response = await apiClient.get<{ unreadCount: number }>(
          API_ENDPOINTS.MINE_CLIENT.MINE_NOTIFICATIONS_UNREAD_COUNT(mineId)
        );

        return response.unreadCount;
      },
      enabled: (options?.enabled ?? true) && mineId.length > 0,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: true,
      refetchInterval: 60 * 1000,
    })),
  });
}

/**
 * The mine's reconciliation queue — every expectation the product has asserted about this mine's
 * citations and where each stands against MSHA's weekly feed (sprint-231, 4C).
 *
 * 🔴 Admin-only on the server (the resolve act is an Admin's); the caller gates rendering the
 * same way the deadline-policy panel does, from the server-backed mine roles.
 */
export function useClientExpectedStates(
  mineId: string,
  paging: PagingRequest,
  options?: { enabled?: boolean }
): UseQueryResult<PagedResult<ClientExpectedStateDto>, Error> {
  return useQuery<PagedResult<ClientExpectedStateDto>, Error>({
    queryKey: mineClientKeys.expectedStates(mineId, paging),
    queryFn: async (): Promise<PagedResult<ClientExpectedStateDto>> =>
      apiClient.get<PagedResult<ClientExpectedStateDto>>(
        API_ENDPOINTS.MINE_CLIENT.MINE_EXPECTED_STATES(mineId),
        { params: { page: paging.page, pageSize: paging.pageSize } }
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * The mine's held-citation register — the enforcement documents nobody can currently work, and
 * the ones a later reading of MSHA's record moved (sprint-236/S003, 4D).
 *
 * 🔴 Admin-only on the server. Every row's next act is an Admin's — correct a transcription,
 * assign withdrawal work, or handle the document outside the product — so the caller gates
 * rendering from the server-backed mine roles, the standing rule.
 *
 * ⚠️ Invalidated by every client write (they all clear the `all` tree), which matters more here
 * than elsewhere: confirming a 7000-3 is what puts a row on this register in the first place.
 */
export function useClientHeldCitations(
  mineId: string,
  paging: PagingRequest,
  options?: { enabled?: boolean }
): UseQueryResult<PagedResult<ClientHeldCitationDto>, Error> {
  return useQuery<PagedResult<ClientHeldCitationDto>, Error>({
    queryKey: mineClientKeys.heldCitations(mineId, paging),
    queryFn: async (): Promise<PagedResult<ClientHeldCitationDto>> =>
      apiClient.get<PagedResult<ClientHeldCitationDto>>(
        API_ENDPOINTS.MINE_CLIENT.MINE_HELD_CITATIONS(mineId),
        { params: { page: paging.page, pageSize: paging.pageSize } }
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });
}

/* ── Writes ─────────────────────────────────────────────────────────────────────────────── */
//
// Every mutation below invalidates the whole client cache tree rather than one key, for the
// reason the 1D writes above give: more than one surface changes and they are not all the same
// query. Completing a task moves the task, the notification feed and the unread badge at once.

export interface AssignAbatementTaskVariables {
  mineId: string;
  violationResponseId: string;
  body: AssignAbatementTaskRequest;
}

/**
 * Creates a task and assigns it in one act.
 *
 * 🔴 There is no reassign endpoint, and this is not an oversight to route around: assigning IS
 * creating. The route names the VIOLATION RESPONSE because the task does not exist yet.
 *
 * ⚠️ The assignee must hold an active SUPERVISOR membership at this mine — the server checks a
 * live row, not a token claim, so a revoked person is refused even mid-session. Filter the
 * picker on `isActive` and on the Supervisor role, or this returns a 400 the operator cannot act
 * on.
 */
export function useAssignAbatementTask(): UseMutationResult<
  { abatementTaskId: string },
  Error,
  AssignAbatementTaskVariables
> {
  const queryClient = useQueryClient();

  return useMutation<{ abatementTaskId: string }, Error, AssignAbatementTaskVariables>({
    mutationFn: async (variables): Promise<{ abatementTaskId: string }> =>
      apiClient.post<{ abatementTaskId: string }>(
        API_ENDPOINTS.MINE_CLIENT.ASSIGN_ABATEMENT_TASK(
          variables.mineId,
          variables.violationResponseId
        ),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

// ── sprint-269 (S005) — the inspection triage surface ────────────────────────────────────────

/**
 * The triage tree for one inspection event: stops → observations (issues) → evidence, grouped
 * and URGENT-FIRST ordered by the SERVER — one call, one order every consumer agrees on. The
 * app's first /api/v2 read.
 *
 * Short staleTime on purpose: this is the live surface an Admin watches while the walk is
 * still draining from the Supervisor's phone.
 */
/**
 * The v2 inspection list (S006/D1) — the triage picker's source. The SERVER orders it newest
 * declared first (`audit.createdAtUtc` desc), so `items[0]` is the walk being watched during a
 * live inspection; the v1 list's BeginDate ordering ties on same-day declares. staleTime
 * matches the triage read: a walk declared mid-watch must reach the picker on the next tick.
 */
export function useClientInspectionEventsV2(
  mineId: string,
  options?: { enabled?: boolean }
): UseQueryResult<PagedV2Response<InspectionEventV2Dto>, Error> {
  return useQuery<PagedV2Response<InspectionEventV2Dto>, Error>({
    queryKey: mineClientKeys.inspectionEventsV2(mineId),
    queryFn: async (): Promise<PagedV2Response<InspectionEventV2Dto>> =>
      apiClient.get<PagedV2Response<InspectionEventV2Dto>>(
        API_ENDPOINTS.MINE_CLIENT.INSPECTION_EVENTS_V2(mineId),
        RENDERS_OWN_ERROR
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useClientInspectionTriage(
  mineId: string,
  inspectionEventId: string | null,
  options?: { enabled?: boolean }
): UseQueryResult<InspectionTriageDto, Error> {
  return useQuery<InspectionTriageDto, Error>({
    queryKey: mineClientKeys.inspectionTriage(mineId, inspectionEventId ?? ''),
    queryFn: async (): Promise<InspectionTriageDto> =>
      apiClient.get<InspectionTriageDto>(
        API_ENDPOINTS.MINE_CLIENT.INSPECTION_TRIAGE(mineId, inspectionEventId ?? ''),
        RENDERS_OWN_ERROR
      ),
    enabled:
      (options?.enabled ?? true) && mineId.length > 0 && (inspectionEventId?.length ?? 0) > 0,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
    refetchOnWindowFocus: true,
  });
}

export interface AssignTaskFromObservationVariables {
  mineId: string;
  observationId: string;
  body: AssignTaskFromObservationRequest;
}

/**
 * The pre-citation assign (S005/D6): open abatement work from an inspection-day observation,
 * BEFORE the 7000-3 exists. The task anchors to the observation and re-anchors to the
 * violation response when the citation lands. Same assignee rule as the classic assign: an
 * active SUPERVISOR membership, checked live server-side.
 */
export function useAssignTaskFromObservation(): UseMutationResult<
  TaskAssignedDto,
  Error,
  AssignTaskFromObservationVariables
> {
  const queryClient = useQueryClient();

  return useMutation<TaskAssignedDto, Error, AssignTaskFromObservationVariables>({
    mutationFn: async (variables): Promise<TaskAssignedDto> =>
      apiClient.post<TaskAssignedDto>(
        API_ENDPOINTS.MINE_CLIENT.ASSIGN_TASK_FROM_OBSERVATION(
          variables.mineId,
          variables.observationId
        ),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export interface AssignNoCitationTaskVariables {
  mineId: string;
  body: AssignTaskFromObservationRequest & { kind: NoCitationTaskKind };
}

/**
 * sprint-277/S002/D1–D3 — assign pre-citation or proactive work from the Tasks tab, with no
 * citation and no capture. Same assignee rule as every assign: an active Supervisor, checked live.
 */
export function useAssignNoCitationTask(): UseMutationResult<
  TaskAssignedDto,
  Error,
  AssignNoCitationTaskVariables
> {
  const queryClient = useQueryClient();

  return useMutation<TaskAssignedDto, Error, AssignNoCitationTaskVariables>({
    mutationFn: async (variables): Promise<TaskAssignedDto> =>
      apiClient.post<TaskAssignedDto>(
        API_ENDPOINTS.MINE_CLIENT.ASSIGN_NO_CITATION_TASK(variables.mineId),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export interface LinkTaskToCitationVariables {
  mineId: string;
  abatementTaskId: string;
  body: LinkTaskToCitationRequest;
}

/**
 * sprint-277/S002/D4 — the Admin links a pre-citation or proactive task to the citation its
 * 7000-3 turned out to be. Invalidates the whole client area: the task, its citation's task
 * count and the held-citation register all move.
 */
export function useLinkTaskToCitation(): UseMutationResult<
  TaskLinkedDto,
  Error,
  LinkTaskToCitationVariables
> {
  const queryClient = useQueryClient();

  return useMutation<TaskLinkedDto, Error, LinkTaskToCitationVariables>({
    mutationFn: async (variables): Promise<TaskLinkedDto> =>
      apiClient.post<TaskLinkedDto>(
        API_ENDPOINTS.MINE_CLIENT.LINK_TASK_TO_CITATION(
          variables.mineId,
          variables.abatementTaskId
        ),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export interface UploadTaskEvidenceVariables {
  mineId: string;
  abatementTaskId: string;
  file: File;
}

/**
 * Stores one evidence file and returns the reference the completion cites.
 *
 * 🔴 THE FIRST HALF OF COMPLETING A TASK, and before sprint-227 it did not exist — which is why
 * no Supervisor could complete a task in a browser at all. The completion endpoint requires
 * evidence and accepts it as a reference to bytes ALREADY in the private evidence container.
 * That is right for the Phase 2 field device, which uploads on capture, and left the browser
 * with nothing to send.
 *
 * ⚠️ The `blobUrl` this returns is an opaque handle, not a download link — the container is
 * private and no SAS is minted. Pass it straight to `useCompleteAbatementTask`.
 *
 * Deliberately does NOT invalidate the cache: nothing an operator can see has changed yet. The
 * task is not complete until the completion call cites this.
 */
export function useUploadTaskEvidence(): UseMutationResult<
  UploadedTaskEvidence,
  Error,
  UploadTaskEvidenceVariables
> {
  return useMutation<UploadedTaskEvidence, Error, UploadTaskEvidenceVariables>({
    mutationFn: async (variables): Promise<UploadedTaskEvidence> => {
      const formData = new FormData();
      formData.append('file', variables.file);

      return apiClient.postForm<UploadedTaskEvidence>(
        API_ENDPOINTS.MINE_CLIENT.UPLOAD_TASK_EVIDENCE(
          variables.mineId,
          variables.abatementTaskId
        ),
        formData
      );
    },
  });
}

export interface CompleteAbatementTaskVariables {
  mineId: string;
  abatementTaskId: string;
  body: CompleteAbatementTaskRequest;
}

export interface CompleteAbatementTaskResponse {
  abatementTaskId: string;
  status: string;
  evidenceCount: number;
}

/**
 * The Supervisor declares the work done, citing the evidence for it.
 *
 * 🔴 At least one evidence item is REQUIRED, and that is the endpoint's reason for existing
 * rather than a field check: completing is the operator asserting a cited condition has been
 * corrected, against a legally-binding MSHA deadline, in a system whose next phase exists to send
 * these records to MSHA. Upload first with `useUploadTaskEvidence`, then send what it returned.
 *
 * ⚠️ §5.7 — what this writes renders as "Operator reported complete", never as "Abated". Nothing
 * it does touches MSHA's clock or MSHA's record.
 */
export function useCompleteAbatementTask(): UseMutationResult<
  CompleteAbatementTaskResponse,
  Error,
  CompleteAbatementTaskVariables
> {
  const queryClient = useQueryClient();

  return useMutation<CompleteAbatementTaskResponse, Error, CompleteAbatementTaskVariables>({
    mutationFn: async (variables): Promise<CompleteAbatementTaskResponse> =>
      apiClient.post<CompleteAbatementTaskResponse>(
        API_ENDPOINTS.MINE_CLIENT.COMPLETE_ABATEMENT_TASK(
          variables.mineId,
          variables.abatementTaskId
        ),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export interface ReportTaskProgressVariables {
  mineId: string;
  abatementTaskId: string;
  body: ReportTaskProgressRequest;
}

/**
 * The Supervisor files progress evidence against a task that is not finished (sprint-228 — F5's
 * middle outcome: complete / blocked / partial).
 *
 * ⚠️ No status changes and nothing is claimed. The evidence rows — each carrying its description —
 * ARE the report, which is why the server requires at least one item.
 */
export function useReportTaskProgress(): UseMutationResult<
  { abatementTaskId: string; evidenceCount: number },
  Error,
  ReportTaskProgressVariables
> {
  const queryClient = useQueryClient();

  return useMutation<
    { abatementTaskId: string; evidenceCount: number },
    Error,
    ReportTaskProgressVariables
  >({
    mutationFn: async (variables): Promise<{ abatementTaskId: string; evidenceCount: number }> =>
      apiClient.post<{ abatementTaskId: string; evidenceCount: number }>(
        API_ENDPOINTS.MINE_CLIENT.REPORT_TASK_PROGRESS(
          variables.mineId,
          variables.abatementTaskId
        ),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export interface PushBackAbatementTaskVariables {
  mineId: string;
  abatementTaskId: string;
  body: PushBackAbatementTaskRequest;
}

/**
 * The assignee reports that they cannot do the job.
 *
 * ⚠️ Only THE ASSIGNEE may file this, and the server answers 403 — not 404 — to anyone else. A
 * colleague filing on their behalf would record an assertion about capacity its author never
 * made, against a legal deadline. Do not offer this control to a user who is not the assignee.
 *
 * ⚠️ §5.7 — a pushback does not pause, extend or reset MSHA's abatement deadline. No surface may
 * imply that returning work buys time.
 */
export function usePushBackAbatementTask(): UseMutationResult<
  { abatementTaskPushbackId: string },
  Error,
  PushBackAbatementTaskVariables
> {
  const queryClient = useQueryClient();

  return useMutation<{ abatementTaskPushbackId: string }, Error, PushBackAbatementTaskVariables>({
    mutationFn: async (variables): Promise<{ abatementTaskPushbackId: string }> =>
      apiClient.post<{ abatementTaskPushbackId: string }>(
        API_ENDPOINTS.MINE_CLIENT.PUSH_BACK_ABATEMENT_TASK(
          variables.mineId,
          variables.abatementTaskId
        ),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export interface ReissueAbatementTaskVariables {
  mineId: string;
  abatementTaskId: string;
  body: ReissueAbatementTaskRequest;
}

/**
 * The Admin puts returned work back.
 *
 * Admin-only, unlike the pushback beside it: returning work is the assignee's call and putting it
 * back is the Admin's, because the Admin is the one who can clear the blocker.
 *
 * ⚠️ Deliberately NOT gated on the resolution chain being finished. Requiring a tidy chain would
 * teach people to falsify step statuses to unblock real work.
 */
export function useReissueAbatementTask(): UseMutationResult<
  { abatementTaskId: string },
  Error,
  ReissueAbatementTaskVariables
> {
  const queryClient = useQueryClient();

  return useMutation<{ abatementTaskId: string }, Error, ReissueAbatementTaskVariables>({
    mutationFn: async (variables): Promise<{ abatementTaskId: string }> =>
      apiClient.post<{ abatementTaskId: string }>(
        API_ENDPOINTS.MINE_CLIENT.REISSUE_ABATEMENT_TASK(
          variables.mineId,
          variables.abatementTaskId
        ),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export interface WithdrawAbatementTaskVariables {
  mineId: string;
  abatementTaskId: string;
  /** Required: it stays on the record and the Supervisor is told it. */
  reason: string;
}

/**
 * The Admin withdraws open work — assigned by mistake, or no longer needed (sprint-277 S007/D6,
 * finding F13). Admin-only; the task is kept as Withdrawn and leaves the active lists.
 */
export function useWithdrawAbatementTask(): UseMutationResult<
  { abatementTaskId: string },
  Error,
  WithdrawAbatementTaskVariables
> {
  const queryClient = useQueryClient();

  return useMutation<{ abatementTaskId: string }, Error, WithdrawAbatementTaskVariables>({
    mutationFn: async (variables): Promise<{ abatementTaskId: string }> =>
      apiClient.post<{ abatementTaskId: string }>(
        API_ENDPOINTS.MINE_CLIENT.WITHDRAW_ABATEMENT_TASK(
          variables.mineId,
          variables.abatementTaskId
        ),
        { reason: variables.reason }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export interface ReviewAbatementTaskVariables {
  mineId: string;
  abatementTaskId: string;
  body: {
    /** 'Accepted' or 'Rejected'. */
    outcome: 'Accepted' | 'Rejected';
    /** 🔴 Required on rejection — it is the whole message the Supervisor acts on. */
    reason?: string;
  };
}

export interface ReviewAbatementTaskResponse {
  abatementTaskId: string;
  outcome: string;
  status: string;
}

/**
 * The Admin answers a filed completion claim — accepts it for submission or rejects it with the
 * reason (sprint-230, 4B — Admin-only, like assign and re-issue).
 *
 * ⚠️ §5.7 at its sharpest on the calling surface: render the acceptance as "Admin reviewed —
 * operator report accepted for submission" (the pinned label), never as a bare "Approved" — the
 * citation stands on MSHA's record until MSHA terminates it, and this act says nothing about that.
 */
export function useReviewAbatementTask(): UseMutationResult<
  ReviewAbatementTaskResponse,
  Error,
  ReviewAbatementTaskVariables
> {
  const queryClient = useQueryClient();

  return useMutation<ReviewAbatementTaskResponse, Error, ReviewAbatementTaskVariables>({
    mutationFn: async (variables): Promise<ReviewAbatementTaskResponse> =>
      apiClient.post<ReviewAbatementTaskResponse>(
        API_ENDPOINTS.MINE_CLIENT.REVIEW_ABATEMENT_TASK(
          variables.mineId,
          variables.abatementTaskId
        ),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export interface ResolveExpectedStateVariables {
  mineId: string;
  expectedStateId: string;
  body: {
    /** 🔴 Required — the record must say why a person closed what the feed could not. */
    note: string;
  };
}

export interface ResolveExpectedStateResponse {
  expectedStateId: string;
  status: string;
}

/**
 * An Admin resolves a reconciliation row with a stated note (sprint-231, 4C — §5.8's human exit).
 *
 * The only routine exit from a mismatch: the comparison step closes rows only by observing the
 * awaited fact in a later feed. Terminal rows (Confirmed/Vacated/Resolved) answer 409 — resolving
 * one would overwrite what the feed observed.
 */
export function useResolveExpectedState(): UseMutationResult<
  ResolveExpectedStateResponse,
  Error,
  ResolveExpectedStateVariables
> {
  const queryClient = useQueryClient();

  return useMutation<ResolveExpectedStateResponse, Error, ResolveExpectedStateVariables>({
    mutationFn: async (variables): Promise<ResolveExpectedStateResponse> =>
      apiClient.post<ResolveExpectedStateResponse>(
        API_ENDPOINTS.MINE_CLIENT.RESOLVE_EXPECTED_STATE(
          variables.mineId,
          variables.expectedStateId
        ),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export interface AddTaskResolutionStepVariables {
  mineId: string;
  abatementTaskPushbackId: string;
  body: AddTaskResolutionStepRequest;
}

export interface AddTaskResolutionStepResponse {
  taskResolutionStepId: string;
  sequence: number;
}

/**
 * Adds a step to a pushback's resolution chain.
 *
 * The route names the EPISODE, not the task: a task pushed back twice has two chains, and a step
 * posted to the task would have to guess which one it belongs to.
 *
 * ⚠️ `expectedResolutionDateUtc` is an ESTIMATE of when the step clears — never a deadline, and
 * emphatically not the internal deadline. Do not render it as a commitment.
 */
export function useAddTaskResolutionStep(): UseMutationResult<
  AddTaskResolutionStepResponse,
  Error,
  AddTaskResolutionStepVariables
> {
  const queryClient = useQueryClient();

  return useMutation<AddTaskResolutionStepResponse, Error, AddTaskResolutionStepVariables>({
    mutationFn: async (variables): Promise<AddTaskResolutionStepResponse> =>
      apiClient.post<AddTaskResolutionStepResponse>(
        API_ENDPOINTS.MINE_CLIENT.ADD_TASK_RESOLUTION_STEP(
          variables.mineId,
          variables.abatementTaskPushbackId
        ),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export interface UpdateTaskResolutionStepVariables {
  mineId: string;
  taskResolutionStepId: string;
  body: UpdateTaskResolutionStepRequest;
}

export interface UpdateTaskResolutionStepResponse {
  taskResolutionStepId: string;
  status: string;
}

/**
 * Moves one resolution step's status.
 *
 * ⚠️ Refused once the episode is closed, and the server answers 409 rather than 404 — the step
 * demonstrably exists and the caller may see it; they simply may not edit the record of a blocker
 * that has already cleared. The chain is an account of what was done, and editing it afterwards
 * rewrites history rather than recording it.
 */
export function useUpdateTaskResolutionStep(): UseMutationResult<
  UpdateTaskResolutionStepResponse,
  Error,
  UpdateTaskResolutionStepVariables
> {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateTaskResolutionStepResponse,
    Error,
    UpdateTaskResolutionStepVariables
  >({
    mutationFn: async (variables): Promise<UpdateTaskResolutionStepResponse> =>
      apiClient.post<UpdateTaskResolutionStepResponse>(
        API_ENDPOINTS.MINE_CLIENT.UPDATE_TASK_RESOLUTION_STEP(
          variables.mineId,
          variables.taskResolutionStepId
        ),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

// ── sprint-279 S004 (epic-042 Phase E, F23 slice 5): outside contacts on a resolution step ──
//
// Every write invalidates `mineClientKeys.all`, the chain's existing rule: the task detail (owner,
// ball-in-court, risk banner), the open timeline and the contact list all move together. Each
// mutation renders its own fixed sentence on failure, so the global toast is off.

/**
 * The mine's organization's active and inactive outside contacts. Admin-only server-side; a mine
 * with no linked organization answers 404, which the panel says in words.
 */
export function useExternalContacts(
  mineId: string,
  options?: { enabled?: boolean }
): UseQueryResult<ExternalContactDto[], Error> {
  return useQuery<ExternalContactDto[], Error>({
    queryKey: mineClientKeys.externalContacts(mineId),
    queryFn: async (): Promise<ExternalContactDto[]> =>
      apiClient.get<ExternalContactDto[]>(
        API_ENDPOINTS.MINE_CLIENT.EXTERNAL_CONTACTS(mineId),
        RENDERS_OWN_ERROR
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0,
    retry: retryUnlessNotFound,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export interface AddExternalContactVariables {
  mineId: string;
  body: AddExternalContactRequest;
}

/** Adds a contact to the organization's directory (Admin-only). 409 = that email is active. */
export function useAddExternalContact(): UseMutationResult<
  { externalContactId: number },
  Error,
  AddExternalContactVariables
> {
  const queryClient = useQueryClient();

  return useMutation<{ externalContactId: number }, Error, AddExternalContactVariables>({
    mutationFn: async (variables): Promise<{ externalContactId: number }> =>
      apiClient.post<{ externalContactId: number }>(
        API_ENDPOINTS.MINE_CLIENT.EXTERNAL_CONTACTS(variables.mineId),
        variables.body,
        RENDERS_OWN_ERROR
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export interface DeactivateExternalContactVariables {
  mineId: string;
  externalContactId: number;
}

/** Deactivates a contact: every link they hold is revoked and their open steps return. */
export function useDeactivateExternalContact(): UseMutationResult<
  { revokedLinks: number },
  Error,
  DeactivateExternalContactVariables
> {
  const queryClient = useQueryClient();

  return useMutation<{ revokedLinks: number }, Error, DeactivateExternalContactVariables>({
    mutationFn: async (variables): Promise<{ revokedLinks: number }> =>
      apiClient.post<{ revokedLinks: number }>(
        API_ENDPOINTS.MINE_CLIENT.DEACTIVATE_EXTERNAL_CONTACT(
          variables.mineId,
          variables.externalContactId
        ),
        undefined,
        RENDERS_OWN_ERROR
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export interface ExternalStepRequestVariables {
  mineId: string;
  taskResolutionStepId: string;
  body: ExternalStepRequestBody;
}

export interface ExternalStepActVariables {
  mineId: string;
  taskResolutionStepId: string;
}

/** Assigns the step to an outside contact and emails them a link (Admin-only). */
export function useSendExternalStepRequest(): UseMutationResult<
  { linkExpiresAtUtc: string },
  Error,
  ExternalStepRequestVariables
> {
  const queryClient = useQueryClient();

  return useMutation<{ linkExpiresAtUtc: string }, Error, ExternalStepRequestVariables>({
    mutationFn: async (variables): Promise<{ linkExpiresAtUtc: string }> =>
      apiClient.post<{ linkExpiresAtUtc: string }>(
        API_ENDPOINTS.MINE_CLIENT.EXTERNAL_STEP_REQUEST(
          variables.mineId,
          variables.taskResolutionStepId
        ),
        variables.body,
        RENDERS_OWN_ERROR
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

/** Revokes the current link and emails the same contact a new one (Admin-only). */
export function useResendExternalStepRequest(): UseMutationResult<
  { linkExpiresAtUtc: string },
  Error,
  ExternalStepActVariables
> {
  const queryClient = useQueryClient();

  return useMutation<{ linkExpiresAtUtc: string }, Error, ExternalStepActVariables>({
    mutationFn: async (variables): Promise<{ linkExpiresAtUtc: string }> =>
      apiClient.post<{ linkExpiresAtUtc: string }>(
        API_ENDPOINTS.MINE_CLIENT.EXTERNAL_STEP_REQUEST_RESEND(
          variables.mineId,
          variables.taskResolutionStepId
        ),
        undefined,
        RENDERS_OWN_ERROR
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

/** Revokes the step's outside link; the step returns to the Admin (Admin-only). */
export function useRevokeExternalStepRequest(): UseMutationResult<
  { revoked: boolean },
  Error,
  ExternalStepActVariables
> {
  const queryClient = useQueryClient();

  return useMutation<{ revoked: boolean }, Error, ExternalStepActVariables>({
    mutationFn: async (variables): Promise<{ revoked: boolean }> =>
      apiClient.post<{ revoked: boolean }>(
        API_ENDPOINTS.MINE_CLIENT.EXTERNAL_STEP_REQUEST_REVOKE(
          variables.mineId,
          variables.taskResolutionStepId
        ),
        undefined,
        RENDERS_OWN_ERROR
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

/**
 * Renders the request email without sending it. A POST that writes nothing, so it invalidates
 * nothing.
 */
export function usePreviewExternalStepRequest(): UseMutationResult<
  ExternalStepRequestPreviewDto,
  Error,
  ExternalStepRequestVariables
> {
  return useMutation<ExternalStepRequestPreviewDto, Error, ExternalStepRequestVariables>({
    mutationFn: async (variables): Promise<ExternalStepRequestPreviewDto> =>
      apiClient.post<ExternalStepRequestPreviewDto>(
        API_ENDPOINTS.MINE_CLIENT.EXTERNAL_STEP_REQUEST_PREVIEW(
          variables.mineId,
          variables.taskResolutionStepId
        ),
        variables.body,
        RENDERS_OWN_ERROR
      ),
  });
}

export interface SetTaskResolutionStepEtaVariables {
  mineId: string;
  taskResolutionStepId: string;
  body: SetTaskResolutionStepEtaRequest;
}

/**
 * Sets a step's expected resolution date — any client member. ⚠️ Moves the ESTIMATE only, never
 * MSHA's date; a date past MSHA's window raises the decision notice server-side.
 */
export function useSetTaskResolutionStepEta(): UseMutationResult<
  { taskResolutionStepId: string; expectedResolutionDateUtc: string | null },
  Error,
  SetTaskResolutionStepEtaVariables
> {
  const queryClient = useQueryClient();

  return useMutation<
    { taskResolutionStepId: string; expectedResolutionDateUtc: string | null },
    Error,
    SetTaskResolutionStepEtaVariables
  >({
    mutationFn: async (
      variables
    ): Promise<{ taskResolutionStepId: string; expectedResolutionDateUtc: string | null }> =>
      apiClient.post<{ taskResolutionStepId: string; expectedResolutionDateUtc: string | null }>(
        API_ENDPOINTS.MINE_CLIENT.TASK_RESOLUTION_STEP_ETA(
          variables.mineId,
          variables.taskResolutionStepId
        ),
        variables.body,
        RENDERS_OWN_ERROR
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

/** One step's timeline, oldest first. Lazy: pass `enabled` only once the timeline is opened. */
export function useTaskResolutionStepActivity(
  mineId: string,
  taskResolutionStepId: string,
  options?: { enabled?: boolean }
): UseQueryResult<ClientStepActivityDto[], Error> {
  return useQuery<ClientStepActivityDto[], Error>({
    queryKey: mineClientKeys.stepActivity(mineId, taskResolutionStepId),
    queryFn: async (): Promise<ClientStepActivityDto[]> =>
      apiClient.get<ClientStepActivityDto[]>(
        API_ENDPOINTS.MINE_CLIENT.TASK_RESOLUTION_STEP_ACTIVITY(mineId, taskResolutionStepId),
        RENDERS_OWN_ERROR
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0 && taskResolutionStepId.length > 0,
    retry: retryUnlessNotFound,
  });
}

export interface MarkNotificationReadVariables {
  mineId: string;
  notificationId: string;
}

export interface MarkNotificationReadResponse {
  notificationId: string;
  readAtUtc: string;
}

/**
 * Records that this user actually read one notification.
 *
 * 🔴 A deliberate act, never a side effect of rendering the feed. What it writes is the only
 * proof-of-receipt the platform has — email stops at Delivered (DR-029) and Push has no client —
 * so the escalation sweep that warns an Admin their Supervisor never came back reads this column
 * and nothing else. Marking read automatically on list would mean nothing is ever escalated;
 * never marking read would mean everything is.
 */
export function useMarkNotificationRead(): UseMutationResult<
  MarkNotificationReadResponse,
  Error,
  MarkNotificationReadVariables
> {
  const queryClient = useQueryClient();

  return useMutation<MarkNotificationReadResponse, Error, MarkNotificationReadVariables>({
    mutationFn: async (variables): Promise<MarkNotificationReadResponse> =>
      apiClient.post<MarkNotificationReadResponse>(
        API_ENDPOINTS.MINE_CLIENT.MARK_NOTIFICATION_READ(
          variables.mineId,
          variables.notificationId
        )
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

/* ── Phase 4 (sprint-229, increment 4A) — the policy surface ────────────────────────────── */

/**
 * The mine's internal-deadline ladder (increment 3E's read, first consumed here — 4A/F6).
 *
 * ⚠️ `null` is the ordinary starting state, not an error: a mine with no policy yet answers
 * 204 No Content, and every task there shows "No target set" until an Admin writes one. Render
 * it as an invitation, never as a fault. Axios surfaces a 204 body as `''`, which is neither
 * `null` nor a policy — normalised to `null` here so consumers get exactly one no-policy shape
 * (unnormalised, the page's `policy === null` card never fired and a Supervisor read NaN%
 * rungs off the empty string — the S003 walk finding).
 */
export function useInternalDeadlinePolicy(
  mineId: string
): UseQueryResult<InternalDeadlinePolicyDto | null, Error> {
  return useQuery<InternalDeadlinePolicyDto | null, Error>({
    queryKey: mineClientKeys.internalDeadlinePolicy(mineId),
    queryFn: async (): Promise<InternalDeadlinePolicyDto | null> => {
      const dto = await apiClient.get<InternalDeadlinePolicyDto | null | ''>(
        API_ENDPOINTS.MINE_CLIENT.INTERNAL_DEADLINE_POLICY(mineId)
      );
      return dto === '' || dto === null || dto === undefined ? null : dto;
    },
    enabled: mineId.length > 0,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

export interface SetInternalDeadlinePolicyVariables {
  mineId: string;
  body: SetInternalDeadlinePolicyRequest;
}

/**
 * Writes the mine's ladder — Admin-only on the server (increment 3E's write, first callable from
 * a browser here).
 *
 * 🔴 Invalidates the whole client tree, and here that is not the usual cautious sweep: the write's
 * second job is recomputing every live task's internal target at this mine, so the task list, the
 * task details and this policy read all changed at once. The response's `tasksRecalculated` is the
 * only visible evidence of that second job — surfaces should say the number out loud.
 *
 * ⚠️ A 400 whose message names the mine's state means the state spans two timezones and D2 refuses
 * to guess — resubmit with an explicit `timeZoneId`. The server's message says exactly that, so
 * render it beside the timezone control rather than as a generic failure.
 */
export interface CreateInspectionEventVariables {
  mineId: string;
  body: CreateInspectionEventRequest;
}

/**
 * A mine Admin opens an inspection from the browser (sprint-229, 4A/N1) — the write J1's chain
 * starts at. Until this hook, every inspection the browser flow ran against was seeded by a
 * device or written straight into the database.
 *
 * 🔴 No `eventNo` travels, ever — the server refuses it. MSHA assigns the number and the 7000-3
 * attach backfills it, so a just-created inspection legitimately renders "Not assigned".
 *
 * No `inspectionEventId` either: that field is a device's offline-retry idempotency key
 * (ADR-002 §4). A browser omits it and the server mints the id, returned in the response so the
 * page can select what it just created.
 */
export function useCreateInspectionEvent(): UseMutationResult<
  CreateInspectionEventResponse,
  Error,
  CreateInspectionEventVariables
> {
  const queryClient = useQueryClient();

  return useMutation<CreateInspectionEventResponse, Error, CreateInspectionEventVariables>({
    mutationFn: async (variables): Promise<CreateInspectionEventResponse> =>
      apiClient.post<CreateInspectionEventResponse>(
        API_ENDPOINTS.MINE_CLIENT.MINE_INSPECTION_EVENTS(variables.mineId),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export function useSetInternalDeadlinePolicy(): UseMutationResult<
  SetInternalDeadlinePolicyResponse,
  Error,
  SetInternalDeadlinePolicyVariables
> {
  const queryClient = useQueryClient();

  return useMutation<
    SetInternalDeadlinePolicyResponse,
    Error,
    SetInternalDeadlinePolicyVariables
  >({
    mutationFn: async (variables): Promise<SetInternalDeadlinePolicyResponse> =>
      apiClient.post<SetInternalDeadlinePolicyResponse>(
        API_ENDPOINTS.MINE_CLIENT.INTERNAL_DEADLINE_POLICY(variables.mineId),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

/* ── sprint-237 (4E-1 Goal 6) — evidence integrity and legal holds ─────────────────────────── */
//
// 🔴 THE READ AND THE TWO WRITES ARE SPLIT ACROSS TWO POLICIES, SERVER-SIDE. Anyone at the mine may
// see whether their evidence is intact and protected — a Supervisor who filed the photograph should
// be able to see it is still the photograph. Only PLACING and CLEARING are Admin. The SPA's
// conditional rendering mirrors that for the sake of a sane screen; it is not the control.

/**
 * What is known about one file: its fingerprint, whether it still verifies, where the fingerprint
 * came from, the retention floor underneath it, and every active legal hold reaching it.
 *
 * 🔴 Hold state is DERIVED by the server — there is no stored flag anywhere behind this. Ask this
 * hook; never infer "not held" from the absence of a field.
 *
 * ⚠️ Pass `evidenceBlobId: null` for a file that has no integrity record. The hook stays disabled
 * and returns no data, which is the honest shape: those files are UNKNOWN, and a surface must say
 * so rather than fall back to a default that reads as either health or failure.
 */
export function useClientEvidenceProvenance(
  mineId: string,
  evidenceBlobId: string | null,
  options?: { enabled?: boolean }
): UseQueryResult<ClientEvidenceProvenanceDto, Error> {
  return useQuery<ClientEvidenceProvenanceDto, Error>({
    queryKey: mineClientKeys.evidenceProvenance(mineId, evidenceBlobId ?? 'none'),
    queryFn: async (): Promise<ClientEvidenceProvenanceDto> =>
      apiClient.get<ClientEvidenceProvenanceDto>(
        API_ENDPOINTS.MINE_CLIENT.EVIDENCE_BLOB_PROVENANCE(mineId, evidenceBlobId ?? '')
      ),
    enabled:
      (options?.enabled ?? true) && mineId.length > 0 && (evidenceBlobId?.length ?? 0) > 0,

    // 🔴 Deliberately NOT cached long. A hold placed from another browser, or verification failing
    // on somebody else's read, changes this answer without anything here doing the changing. The
    // cost of a stale "not held" is an Admin believing evidence is free to change.
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
  });
}

/**
 * The provenance records for a page's whole file set, in one request (sprint-254, A6) — the batch
 * the accepted-cost note on `ClientTaskDetailPage` named as its own fix for one-request-per-file.
 *
 * 🔴 A blob id may be ABSENT from the map, and absence means "no integrity record" (or a blob the
 * caller may not see — the server deliberately does not distinguish). Render an absent file as
 * UNKNOWN, exactly as the single hook's disabled state is rendered — never as health or failure.
 *
 * ⚠️ Ids are sorted into the query key so the same set asked in a different order shares the cache
 * entry. Same freshness posture as the single read, for the same stale-"not held" reason.
 */
export function useClientEvidenceProvenanceBatch(
  mineId: string,
  evidenceBlobIds: string[]
): UseQueryResult<Record<string, ClientEvidenceProvenanceDto>, Error> {
  const sortedIds = [...evidenceBlobIds].sort();

  return useQuery<Record<string, ClientEvidenceProvenanceDto>, Error>({
    queryKey: mineClientKeys.evidenceProvenanceBatch(mineId, sortedIds.join(',')),
    queryFn: async (): Promise<Record<string, ClientEvidenceProvenanceDto>> =>
      apiClient.get<Record<string, ClientEvidenceProvenanceDto>>(
        API_ENDPOINTS.MINE_CLIENT.EVIDENCE_BLOB_PROVENANCE_BATCH(mineId, sortedIds)
      ),
    enabled: mineId.length > 0 && sortedIds.length > 0,
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Mints a short-lived URL that opens one piece of evidence (sprint-244 — 4E-2 Goal 5, DR-040).
 *
 * 🔴 **A MUTATION, NOT A QUERY, AND THAT IS THE WHOLE DESIGN OF THIS HOOK.** Minting is an ACT: it
 * signs a bearer credential and writes a durable `EvidenceAccessEvent` row naming the person who
 * asked. As a `useQuery` it would be cached, refetched on window focus and fired the moment a
 * component mounted — so simply having a task page open would manufacture access records for a
 * photograph nobody opened, and the audit trail would stop meaning anything. It fires when a person
 * clicks, and at no other time.
 *
 * ⚠️ **The result is deliberately not cached anywhere.** `sasUrl` expires in minutes, and a stored
 * one is a credential outliving the moment it was granted for. Use it and drop it; ask again if the
 * person asks again — which is also the honest thing for the audit trail to record.
 */
export function useMintClientEvidenceReadLink(): UseMutationResult<
  ClientEvidenceReadLinkDto,
  Error,
  { mineId: string; evidenceBlobId: string }
> {
  return useMutation<
    ClientEvidenceReadLinkDto,
    Error,
    { mineId: string; evidenceBlobId: string }
  >({
    mutationFn: async ({ mineId, evidenceBlobId }): Promise<ClientEvidenceReadLinkDto> =>
      apiClient.get<ClientEvidenceReadLinkDto>(
        API_ENDPOINTS.MINE_CLIENT.EVIDENCE_BLOB_READ_LINK(mineId, evidenceBlobId)
      ),

    // 🔴 No cache invalidation, deliberately. Nothing the user can see changed — the evidence, its
    // fingerprint and its hold state are all exactly as they were. The only new row is an audit
    // record this lane does not read back.
  });
}

/** The bytes of one evidence image, and the verification state the server read as it served them. */
export interface ClientEvidenceImage {
  blob: Blob;
  verificationState: EvidenceVerificationState | null;
}

export type ClientEvidenceImageFetcher = (
  mineId: string,
  evidenceBlobId: string
) => Promise<ClientEvidenceImage>;

const VERIFICATION_STATES: readonly EvidenceVerificationState[] = [
  'NotFingerprinted',
  'Intact',
  'Mismatch',
  'Resolved',
];

const fetchClientEvidenceImage: ClientEvidenceImageFetcher = async (mineId, evidenceBlobId) => {
  const response = await apiClient.getBlob(
    API_ENDPOINTS.MINE_CLIENT.EVIDENCE_BLOB_IMAGE_CONTENT(mineId, evidenceBlobId),
    // A preview that fails says so quietly in its own row (PREVIEW_UNAVAILABLE_MESSAGE); a global
    // "Request failed with status code 404" toast would announce an error nobody acted to cause.
    { showErrorToast: false }
  );
  const header = response.headers['x-evidence-verification'];
  const verificationState = VERIFICATION_STATES.find((state) => state === header) ?? null;
  return { blob: response.data, verificationState };
};

/**
 * The inline image preview's byte source (sprint-276 — TD-255, S001/D1 + D4).
 *
 * 🔴 **This is the one deliberate exception to the rule above** that evidence is fetched only when a
 * person clicks. The PO ruled at sprint-276/S001 (D1) that every image on the task page shows
 * itself, accepting that each preview writes the same `EvidenceAccessEvent` row an open does — and
 * the row stays truthful, because the photograph IS shown to the person it names.
 *
 * ⚠️ Not a query, for the same reason `useMintClientEvidenceReadLink` is a mutation: TanStack would
 * refetch on focus and manufacture accesses. The page-level preview provider fetches each blob at
 * most once per page view and owns the object URLs. No SAS is involved at all (D4): the API streams
 * the bytes.
 */
export function useClientEvidenceImageFetcher(): ClientEvidenceImageFetcher {
  return fetchClientEvidenceImage;
}

/**
 * The evidence-integrity register (sprint-244 — 4E-2 Goal 4).
 *
 * 🔴 Same short `staleTime` as the provenance read, and for the same reason: a hold placed from
 * another browser changes what belongs on this list without anything here doing the changing, and
 * the cost of a stale answer is an Admin believing a file is free to change.
 */
export function useClientEvidenceIntegrity(
  mineId: string,
  paging: PagingRequest,
  options?: { enabled?: boolean }
): UseQueryResult<PagedResult<ClientEvidenceIntegrityRowDto>, Error> {
  return useQuery<PagedResult<ClientEvidenceIntegrityRowDto>, Error>({
    queryKey: mineClientKeys.evidenceIntegrity(mineId, paging),
    queryFn: async (): Promise<PagedResult<ClientEvidenceIntegrityRowDto>> =>
      apiClient.get<PagedResult<ClientEvidenceIntegrityRowDto>>(
        API_ENDPOINTS.MINE_CLIENT.MINE_EVIDENCE_INTEGRITY(mineId),
        { ...RENDERS_OWN_ERROR, params: { page: paging.page, pageSize: paging.pageSize } }
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0,
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Whether the scheduled checker has run, and whether it reaches this mine.
 *
 * 🔴 Fetched separately from the register rather than folded into it — the register is PAGED, and
 * a standing fact carried on page 1 disappears on page 2.
 */
export function useClientEvidenceSweepStatus(
  mineId: string,
  options?: { enabled?: boolean }
): UseQueryResult<ClientEvidenceSweepStatusDto, Error> {
  return useQuery<ClientEvidenceSweepStatusDto, Error>({
    queryKey: mineClientKeys.evidenceSweepStatus(mineId),
    queryFn: async (): Promise<ClientEvidenceSweepStatusDto> =>
      apiClient.get<ClientEvidenceSweepStatusDto>(
        API_ENDPOINTS.MINE_CLIENT.MINE_EVIDENCE_SWEEP_STATUS(mineId),
        RENDERS_OWN_ERROR
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0,
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
  });
}

export interface PlaceEvidenceLegalHoldVariables {
  mineId: string;
  body: PlaceEvidenceLegalHoldRequest;
}

/**
 * Freezes everything the named scope reaches, indefinitely, until someone deliberately clears it.
 *
 * 🔴 The reason is REQUIRED by DR-038 and the server refuses an empty one with a 400. It is not an
 * optional note: a hold is an assertion that evidence must be preserved for a reason, and the
 * reason is the part a contest reads.
 *
 * ⚠️ 409 means an active hold already names this exact scope — one active hold per scope, enforced
 * by a filtered unique index. That is not an error to retry; it means the freeze is already on.
 */
export function usePlaceEvidenceLegalHold(): UseMutationResult<
  { evidenceLegalHoldId: string },
  Error,
  PlaceEvidenceLegalHoldVariables
> {
  const queryClient = useQueryClient();

  return useMutation<{ evidenceLegalHoldId: string }, Error, PlaceEvidenceLegalHoldVariables>({
    mutationFn: async (variables): Promise<{ evidenceLegalHoldId: string }> =>
      apiClient.post<{ evidenceLegalHoldId: string }>(
        API_ENDPOINTS.MINE_CLIENT.EVIDENCE_HOLDS(variables.mineId),
        variables.body
      ),
    onSuccess: () => {
      // The whole tree, for the reason the key comment gives: a scope reaches files on surfaces
      // nobody has open, and hold state is derived rather than stored on the row being looked at.
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export interface ClearEvidenceLegalHoldVariables {
  mineId: string;
  evidenceLegalHoldId: string;
  body: ClearEvidenceLegalHoldRequest;
}

/**
 * Releases ONE hold.
 *
 * 🔴 THIS NEVER MAKES EVIDENCE DELETABLE. The 7-year retention floor snaps back underneath — the
 * hold was only ever the thing overriding that floor upward. Any copy implying otherwise is wrong.
 *
 * 🔴 AND IT RELEASES ONLY THIS ONE. More than one active hold on the same bytes is normal, and the
 * others stay in force. A surface that says "released" without saying what remains is how somebody
 * concludes evidence under a live investigation is free.
 *
 * ⚠️ DELETE with a required body — the stated reason, exactly as when placing. Axios carries it as
 * `{ data }`; a bare `delete(url)` returns a 400 the operator cannot act on.
 */
export function useClearEvidenceLegalHold(): UseMutationResult<
  void,
  Error,
  ClearEvidenceLegalHoldVariables
> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, ClearEvidenceLegalHoldVariables>({
    mutationFn: async (variables): Promise<void> =>
      apiClient.delete<void>(
        API_ENDPOINTS.MINE_CLIENT.EVIDENCE_HOLD_BY_ID(
          variables.mineId,
          variables.evidenceLegalHoldId
        ),
        { data: variables.body }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

/* ── Who we send it to (sprint-244 — 4E-2 Goal 7) ────────────────────────────────────────────── */

/**
 * The mine's recipient directory: inspectors its citations resolve to, the sightings that resolve
 * to nobody yet, and the field offices it can reach.
 *
 * 🔴 ONE request, not three. The page's whole argument is the RELATIONSHIP between the halves —
 * how many citations are attributed against how many are still questions — and three independently
 * arriving lists would render a page that briefly claims everything is identified.
 *
 * ⚠️ Reading it SEEDS field offices server-side, idempotently. That is what lets the feature avoid
 * a post-ingest hook in a job class two epics are both editing.
 */
export function useClientCorrespondenceDirectory(
  mineId: string,
  options?: { enabled?: boolean }
): UseQueryResult<MineInspectorDirectoryDto, Error> {
  return useQuery<MineInspectorDirectoryDto, Error>({
    queryKey: mineClientKeys.correspondenceDirectory(mineId),
    queryFn: async (): Promise<MineInspectorDirectoryDto> =>
      apiClient.get<MineInspectorDirectoryDto>(
        API_ENDPOINTS.MINE_CLIENT.MINE_CORRESPONDENCE_DIRECTORY(mineId)
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0,
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
  });
}

export interface ConfirmInspectorAliasVariables {
  mineId: string;
  inspectorId: string;
  body: ConfirmInspectorAliasRequest;
}

/**
 * Confirms that an observed spelling is this inspector.
 *
 * 🔴 THIS MUTATION IS THE FEATURE. Without the write, the next 7000-3 carrying the same misspelling
 * scores the same candidates and asks the same question — and the directory would be a confirmation
 * dialog rather than something that learns. The increment's Definition of Done is this call
 * happening once.
 *
 * ⚠️ Idempotent server-side: confirming the same spelling twice is the same answer, not a duplicate.
 */
export function useConfirmInspectorAlias(): UseMutationResult<
  { inspectorNameAliasId: string },
  Error,
  ConfirmInspectorAliasVariables
> {
  const queryClient = useQueryClient();

  return useMutation<{ inspectorNameAliasId: string }, Error, ConfirmInspectorAliasVariables>({
    mutationFn: async (variables): Promise<{ inspectorNameAliasId: string }> =>
      apiClient.post<{ inspectorNameAliasId: string }>(
        API_ENDPOINTS.MINE_CLIENT.CORRESPONDENCE_INSPECTOR_ALIASES(
          variables.mineId,
          variables.inspectorId
        ),
        variables.body
      ),
    onSuccess: () => {
      // The whole tree: an identity is shared, so a confirmation here changes how citations
      // resolve on surfaces nobody has open.
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export interface CreateInspectorVariables {
  mineId: string;
  body: CreateInspectorRequest;
}

/**
 * Adds somebody new to the shared directory.
 *
 * ⚠️ **409 is not a failure to retry.** It means that AR number already belongs to an inspector on
 * file, and the response names them — the surface should offer that person rather than creating a
 * second row. Two rows claiming one AR number would break exact matching permanently and silently.
 */
export function useCreateInspector(): UseMutationResult<
  { inspectorId: string },
  Error,
  CreateInspectorVariables
> {
  const queryClient = useQueryClient();

  return useMutation<{ inspectorId: string }, Error, CreateInspectorVariables>({
    mutationFn: async (variables): Promise<{ inspectorId: string }> =>
      apiClient.post<{ inspectorId: string }>(
        API_ENDPOINTS.MINE_CLIENT.CORRESPONDENCE_INSPECTORS(variables.mineId),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export interface SaveInspectorContactVariables {
  mineId: string;
  inspectorId: string;
  body: SaveInspectorContactRequest;
}

/** This mine's contact details for an inspector — the "never re-types the contact" half. */
export function useSaveInspectorContact(): UseMutationResult<
  void,
  Error,
  SaveInspectorContactVariables
> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, SaveInspectorContactVariables>({
    mutationFn: async (variables): Promise<void> =>
      apiClient.put<void>(
        API_ENDPOINTS.MINE_CLIENT.CORRESPONDENCE_INSPECTOR_CONTACT(
          variables.mineId,
          variables.inspectorId
        ),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export interface SaveFieldOfficeContactVariables {
  mineId: string;
  fieldOfficeId: string;
  body: SaveFieldOfficeContactRequest;
}

/** This mine's postal details for a field office — where the envelope actually goes. */
export function useSaveFieldOfficeContact(): UseMutationResult<
  void,
  Error,
  SaveFieldOfficeContactVariables
> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, SaveFieldOfficeContactVariables>({
    mutationFn: async (variables): Promise<void> =>
      apiClient.put<void>(
        API_ENDPOINTS.MINE_CLIENT.CORRESPONDENCE_FIELD_OFFICE_CONTACT(
          variables.mineId,
          variables.fieldOfficeId
        ),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

// ── sprint-244/S005 (4E-2 Goal 7 part 2) — the envelope; sprint-249/S001 (4F) — the send ───────
//
// Every draft mutation invalidates the packet subtree (`correspondencePackets(mineId)`): the list
// and the one packet change; nothing shared does, so the rest of the mine tree is left alone. The
// send and the reply are different: they write an ExpectedState (the reconciliation tab and the
// citation detail both read it), so they sweep the whole `all` tree.

/** The mine's draft packets, newest-touched first. */
export function useClientCorrespondencePackets(
  mineId: string,
  paging: PagingRequest,
  options?: { enabled?: boolean }
): UseQueryResult<PagedResult<CorrespondencePacketRowDto>, Error> {
  return useQuery<PagedResult<CorrespondencePacketRowDto>, Error>({
    queryKey: mineClientKeys.correspondencePacketList(mineId, paging),
    queryFn: async (): Promise<PagedResult<CorrespondencePacketRowDto>> =>
      apiClient.get<PagedResult<CorrespondencePacketRowDto>>(
        API_ENDPOINTS.MINE_CLIENT.CORRESPONDENCE_PACKETS(mineId),
        { params: { page: paging.page, pageSize: paging.pageSize } }
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0,
    staleTime: 15 * 1000,
  });
}

/** The whole envelope: recipient, exhibits in order, warnings, and the log. */
export function useClientCorrespondencePacket(
  mineId: string,
  packetId: string,
  options?: { enabled?: boolean }
): UseQueryResult<CorrespondencePacketDetailDto, Error> {
  return useQuery<CorrespondencePacketDetailDto, Error>({
    queryKey: mineClientKeys.correspondencePacket(mineId, packetId),
    queryFn: async (): Promise<CorrespondencePacketDetailDto> =>
      apiClient.get<CorrespondencePacketDetailDto>(
        API_ENDPOINTS.MINE_CLIENT.CORRESPONDENCE_PACKET(mineId, packetId)
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0 && packetId.length > 0,
    staleTime: 15 * 1000,
  });
}

/** What may go in the envelope. Fetched only while the picker is open — it is a pick list, not page state. */
export function useClientPacketCandidateEvidence(
  mineId: string,
  packetId: string,
  options?: { enabled?: boolean }
): UseQueryResult<CandidateEvidenceRowDto[], Error> {
  return useQuery<CandidateEvidenceRowDto[], Error>({
    queryKey: mineClientKeys.correspondencePacketCandidates(mineId, packetId),
    queryFn: async (): Promise<CandidateEvidenceRowDto[]> =>
      apiClient.get<CandidateEvidenceRowDto[]>(
        API_ENDPOINTS.MINE_CLIENT.CORRESPONDENCE_PACKET_CANDIDATE_EVIDENCE(mineId, packetId)
      ),
    enabled: (options?.enabled ?? true) && mineId.length > 0 && packetId.length > 0,
    staleTime: 0,
  });
}

export interface CreateCorrespondencePacketVariables {
  mineId: string;
  body: CreateCorrespondencePacketRequest;
}

/** Opens a draft. 404 means the citation or the recipient is not this mine's. */
export function useCreateCorrespondencePacket(): UseMutationResult<
  { correspondencePacketId: string },
  Error,
  CreateCorrespondencePacketVariables
> {
  const queryClient = useQueryClient();

  return useMutation<{ correspondencePacketId: string }, Error, CreateCorrespondencePacketVariables>({
    mutationFn: async (variables): Promise<{ correspondencePacketId: string }> =>
      apiClient.post<{ correspondencePacketId: string }>(
        API_ENDPOINTS.MINE_CLIENT.CORRESPONDENCE_PACKETS(variables.mineId),
        variables.body
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: mineClientKeys.correspondencePackets(variables.mineId),
      });
    },
  });
}

export interface UpdateCorrespondencePacketVariables {
  mineId: string;
  packetId: string;
  body: UpdateCorrespondencePacketRequest;
}

/** Edits a draft's kind, recipient, subject or body. 409 means it is no longer a draft. */
export function useUpdateCorrespondencePacket(): UseMutationResult<
  void,
  Error,
  UpdateCorrespondencePacketVariables
> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, UpdateCorrespondencePacketVariables>({
    mutationFn: async (variables): Promise<void> =>
      apiClient.put<void>(
        API_ENDPOINTS.MINE_CLIENT.CORRESPONDENCE_PACKET(variables.mineId, variables.packetId),
        variables.body
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: mineClientKeys.correspondencePackets(variables.mineId),
      });
    },
  });
}

export interface LinkPacketEvidenceVariables {
  mineId: string;
  packetId: string;
  body: LinkPacketEvidenceRequest;
}

/**
 * Puts one piece of evidence in the envelope.
 *
 * 🔴 The body carries `evidenceBlobId` — the integrity record — never a URL. And the response is a
 * 200 with `warnings`: unreviewed or mismatched evidence is linked AND flagged, never refused
 * (charter D-h). 409 is "already in the envelope".
 */
export function useLinkPacketEvidence(): UseMutationResult<
  LinkedPacketEvidenceResponse,
  Error,
  LinkPacketEvidenceVariables
> {
  const queryClient = useQueryClient();

  return useMutation<LinkedPacketEvidenceResponse, Error, LinkPacketEvidenceVariables>({
    mutationFn: async (variables): Promise<LinkedPacketEvidenceResponse> =>
      apiClient.post<LinkedPacketEvidenceResponse>(
        API_ENDPOINTS.MINE_CLIENT.CORRESPONDENCE_PACKET_EVIDENCE(variables.mineId, variables.packetId),
        variables.body
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: mineClientKeys.correspondencePackets(variables.mineId),
      });
    },
  });
}

export interface UnlinkPacketEvidenceVariables {
  mineId: string;
  packetId: string;
  evidenceBlobId: string;
}

/** Takes one piece of evidence out of the envelope. The bytes and their record are untouched. */
export function useUnlinkPacketEvidence(): UseMutationResult<
  void,
  Error,
  UnlinkPacketEvidenceVariables
> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, UnlinkPacketEvidenceVariables>({
    mutationFn: async (variables): Promise<void> =>
      apiClient.delete<void>(
        API_ENDPOINTS.MINE_CLIENT.CORRESPONDENCE_PACKET_EVIDENCE_ITEM(
          variables.mineId,
          variables.packetId,
          variables.evidenceBlobId
        )
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: mineClientKeys.correspondencePackets(variables.mineId),
      });
    },
  });
}

export interface DiscardCorrespondencePacketVariables {
  mineId: string;
  packetId: string;
}

/** Discards a draft. Its log is kept; the list stops showing it. */
export function useDiscardCorrespondencePacket(): UseMutationResult<
  void,
  Error,
  DiscardCorrespondencePacketVariables
> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, DiscardCorrespondencePacketVariables>({
    mutationFn: async (variables): Promise<void> =>
      apiClient.delete<void>(
        API_ENDPOINTS.MINE_CLIENT.CORRESPONDENCE_PACKET(variables.mineId, variables.packetId)
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: mineClientKeys.correspondencePackets(variables.mineId),
      });
    },
  });
}

export interface MarkPacketSentVariables {
  mineId: string;
  packetId: string;
  body: MarkPacketSentRequest;
}

/**
 * 4F — marks a Draft sent (outside the product, D-b) with channel + proof. Freezes the manifest and
 * opens the packet's feed watch on the server in one write. 409 means it is no longer a Draft.
 *
 * 🔴 Sweeps `all`, not the packet subtree: the watch is an ExpectedState, and the reconciliation
 * tab and the citation detail both read those.
 */
export function useMarkPacketSent(): UseMutationResult<void, Error, MarkPacketSentVariables> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, MarkPacketSentVariables>({
    mutationFn: async (variables): Promise<void> =>
      apiClient.post<void>(
        API_ENDPOINTS.MINE_CLIENT.CORRESPONDENCE_PACKET_SEND(variables.mineId, variables.packetId),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}

export interface RecordPacketReplyVariables {
  mineId: string;
  packetId: string;
  body: RecordPacketReplyRequest;
}

/**
 * 4F — records MSHA's reply. Accepted leaves the watch open; Rejected resolves it on the server and
 * raises a next-steps to-do (charter §4.10 R2). 409 unless the packet is Sent. Sweeps `all` for the
 * same reason as the send.
 */
export function useRecordPacketReply(): UseMutationResult<void, Error, RecordPacketReplyVariables> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, RecordPacketReplyVariables>({
    mutationFn: async (variables): Promise<void> =>
      apiClient.post<void>(
        API_ENDPOINTS.MINE_CLIENT.CORRESPONDENCE_PACKET_REPLY(variables.mineId, variables.packetId),
        variables.body
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mineClientKeys.all });
    },
  });
}
